import {
  changeEventAttendanceAction,
  getEventAttendanceAction,
} from '@/actions/event-attendees'
import DonationCampaignCta from '@/components/_registration/donation-campaign-cta'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Skeleton } from '@/components/ui/skeleton'
import { formatDurationCountdown } from '@/lib/countdown'
import { getDonationCampaign } from '@/lib/donation-campaigns'
import {
  canRecruitForEvent,
  getEffectiveRecruitmentOpenDateTime,
} from '@/lib/recruitment-scheduling'
import { cn } from '@/lib/utils'
import { ChevronDown } from 'lucide-react'
import { useCallback, useEffect, useState } from 'react'
import toast from 'react-hot-toast'
import { mutate as globalMutate } from 'swr'
import ChampionshipAttendeeStatusButton from '../../components/championship-attendee-status-button'

function useEventRegistrationCountdown(event) {
  const [now, setNow] = useState(null)
  const registrationOpenDateTime = getEffectiveRecruitmentOpenDateTime(
    event.championship,
    event,
  )
  const registrationOpenTimestamp = registrationOpenDateTime
    ? new Date(registrationOpenDateTime).getTime()
    : null
  const hasValidRegistrationOpenTimestamp = Number.isFinite(
    registrationOpenTimestamp,
  )

  useEffect(() => {
    if (!hasValidRegistrationOpenTimestamp) return

    const currentNow = Date.now()
    setNow(currentNow)

    if (registrationOpenTimestamp <= currentNow) return

    const intervalId = window.setInterval(() => {
      const nextNow = Date.now()
      setNow(nextNow)

      if (registrationOpenTimestamp <= nextNow) {
        window.clearInterval(intervalId)
      }
    }, 1000)

    return () => window.clearInterval(intervalId)
  }, [hasValidRegistrationOpenTimestamp, registrationOpenTimestamp])

  if (!hasValidRegistrationOpenTimestamp || !now) {
    return {
      isScheduled: false,
      label: null,
    }
  }

  if (registrationOpenTimestamp <= now) {
    return {
      isScheduled: false,
      label: null,
    }
  }

  return {
    isScheduled: true,
    label: formatDurationCountdown(registrationOpenTimestamp - now),
  }
}

export default function EventAttendeeStatusButton({
  event,
  session,
  initialAttendance,
  mutateEventDrivers,
  hideIfRegistered = false,
  enableDonationCampaign = false,
  showRegistrationCountdownOnly = false,
  increaseDriverCount,
  decreaseDriverCount,
}) {
  const sessionUserId = session?.user?.id || null
  const donationCampaign = enableDonationCampaign
    ? getDonationCampaign({
        eventId: event.id,
        championshipId: event.championshipId,
      })
    : null
  const [donationDialogOpen, setDonationDialogOpen] = useState(false)
  const registrationCountdown = useEventRegistrationCountdown(event)
  const eventRegistrationIsOpen = canRecruitForEvent(event.championship, event)
  const hasInitialAttendance = Boolean(initialAttendance)
  const [localAttendance, setLocalAttendance] = useState(
    initialAttendance || null,
  )

  useEffect(() => {
    if (initialAttendance !== undefined) {
      setLocalAttendance(initialAttendance)
    }
  }, [initialAttendance])

  // When not given initialAttendance, fetch from server action
  const [attendanceData, setAttendanceData] = useState(null)
  const [isLoadingAttendance, setIsLoadingAttendance] =
    useState(!hasInitialAttendance)

  const handleRegistrationSuccess = useCallback(() => {
    if (donationCampaign?.autoOpenAfterRegistration) {
      setDonationDialogOpen(true)
    }
  }, [donationCampaign])

  const loadAttendance = useCallback(async () => {
    if (hasInitialAttendance || !sessionUserId) return
    setIsLoadingAttendance(true)
    try {
      const data = await getEventAttendanceAction(event.id)
      setAttendanceData(data)
    } finally {
      setIsLoadingAttendance(false)
    }
  }, [event.id, hasInitialAttendance, sessionUserId])

  useEffect(() => {
    loadAttendance()
  }, [loadAttendance])

  const championshipAttendee = hasInitialAttendance
    ? localAttendance?.championshipAttendee || null
    : attendanceData?.championshipAttendee || null
  const eventAttendee = hasInitialAttendance
    ? localAttendance?.eventAttendee || null
    : attendanceData?.eventAttendee || null

  async function mutateAttendance() {
    if (!sessionUserId) return

    if (!hasInitialAttendance) {
      await loadAttendance()
      return
    }

    const data = await getEventAttendanceAction(event.id)
    setLocalAttendance(
      data.championshipAttendee
        ? {
            championshipAttendee: data.championshipAttendee,
            eventAttendee: data.eventAttendee,
          }
        : null,
    )
  }

  async function joinEvent() {
    if (!eventRegistrationIsOpen) return

    toast.loading('Joining event...', { id: 'join-event' })

    try {
      const result = await changeEventAttendanceAction(
        event.id,
        sessionUserId,
        'attending',
      )

      if (!result.ok) throw new Error(result.message)
      // The protected joining-info control also appears on the race calendar,
      // where its event snapshot does not change with the attendance button.
      void globalMutate(
        (key) =>
          Array.isArray(key) &&
          key[0] === `/api/v4/events/${event.id}/server-info`,
      ).catch(() => undefined)
      handleRegistrationSuccess()

      // Update local SWR cache
      await mutateAttendance()

      // Invalidate all related cache entries across the application
      const attendeeUrl = `community/${event.championship.communityId}/championship/${event.championshipId}/attendee`
      await globalMutate(
        (key) => typeof key === 'object' && key.url?.startsWith(attendeeUrl),
      )

      // Also invalidate event-specific data that other components might be using
      const eventUrl = `community/${event.championship.communityId}/championship/${event.championshipId}/event/${event.id}`
      await globalMutate(
        (key) => typeof key === 'object' && key.url?.startsWith(eventUrl),
      )

      if (mutateEventDrivers) {
        await mutateEventDrivers()
      }

      if (increaseDriverCount) {
        increaseDriverCount()
      }

      toast.success('Joined event!', { id: 'join-event' })
    } catch (e) {
      toast.error(e.message, { id: 'join-event' })
    }
  }

  if (isLoadingAttendance) {
    return (
      <div className="flex h-full w-full gap-4">
        <Skeleton className="h-full w-full rounded-sm" />
      </div>
    )
  }

  if (hideIfRegistered && eventAttendee) {
    return donationCampaign ? (
      <DonationCampaignCta
        campaign={donationCampaign}
        open={donationDialogOpen}
        onOpenChange={setDonationDialogOpen}
      />
    ) : null
  }

  const championshipRegistrationButton = (
    <DonationCampaignCta
      campaign={donationCampaign}
      open={donationDialogOpen}
      onOpenChange={setDonationDialogOpen}
    >
      <ChampionshipAttendeeStatusButton
        championship={event.championship}
        presetAttendee={championshipAttendee}
        skipInitialFetch={hasInitialAttendance}
        deferApplicationQuestions
        mutatePresetAttendee={mutateAttendance}
        mutateDriverCount={mutateEventDrivers}
        increaseDriverCount={increaseDriverCount}
        session={session}
        initialUserIsWhitelisted={initialAttendance?.userIsWhitelisted}
        onRegistrationSuccess={handleRegistrationSuccess}
        registerLabel={
          registrationCountdown.isScheduled
            ? 'Register for Championship'
            : 'Register'
        }
      />
    </DonationCampaignCta>
  )

  return (
    <>
      <div className="flex h-full w-full gap-4">
        <div className="flex h-full min-w-0 flex-1">
          {!championshipAttendee ||
          championshipAttendee.status !== 'accepted' ? (
            registrationCountdown.isScheduled ? (
              showRegistrationCountdownOnly ? (
                <DonationCampaignCta
                  campaign={donationCampaign}
                  open={donationDialogOpen}
                  onOpenChange={setDonationDialogOpen}
                >
                  <Button
                    type="button"
                    variant="outline"
                    className="bg-neutral-blue-800! h-full min-h-[44px] w-full cursor-not-allowed border-neutral-blue-600 px-3 text-xs font-semibold text-neutral-blue-100 disabled:opacity-100"
                    disabled
                  >
                    {registrationCountdown.label}
                  </Button>
                </DonationCampaignCta>
              ) : (
                <div className="flex h-full w-full flex-col gap-3">
                  <Button
                    type="button"
                    variant="outline"
                    className="bg-neutral-blue-800! h-full min-h-[44px] w-full cursor-not-allowed border-neutral-blue-600 px-3 text-xs font-semibold text-neutral-blue-100 disabled:opacity-100"
                    disabled
                  >
                    Event registration opens in {registrationCountdown.label}
                  </Button>

                  <div className="flex h-full min-w-0 flex-1">
                    {championshipRegistrationButton}
                  </div>
                </div>
              )
            ) : (
              championshipRegistrationButton
            )
          ) : !eventAttendee ? (
            <DonationCampaignCta
              campaign={donationCampaign}
              open={donationDialogOpen}
              onOpenChange={setDonationDialogOpen}
            >
              <Button
                type="button"
                className={cn(
                  'h-full w-full disabled:opacity-100',
                  eventRegistrationIsOpen
                    ? 'bg-linear-to-b hover:from-primary-600! hover:to-primary-600! border-none from-primary-500 to-[#FF3D00]'
                    : 'border-neutral-blue-600! bg-neutral-blue-800! text-neutral-blue-100!',
                )}
                data-type="event"
                disabled={!eventRegistrationIsOpen}
                onClick={joinEvent}
              >
                {registrationCountdown.isScheduled
                  ? `Registration opens in ${registrationCountdown.label}`
                  : 'Register'}
              </Button>
            </DonationCampaignCta>
          ) : (
            <DonationCampaignCta
              campaign={donationCampaign}
              open={donationDialogOpen}
              onOpenChange={setDonationDialogOpen}
            >
              <EventAttendanceSelector
                event={event}
                eventAttendee={eventAttendee}
                sessionUserId={sessionUserId}
                mutate={mutateAttendance}
                mutateEventDrivers={mutateEventDrivers}
                increaseDriverCount={increaseDriverCount}
                decreaseDriverCount={decreaseDriverCount}
              />
            </DonationCampaignCta>
          )}
        </div>
      </div>
    </>
  )
}

function EventAttendanceSelector({
  event,
  eventAttendee,
  sessionUserId,
  mutate,
  mutateEventDrivers,
  increaseDriverCount,
  decreaseDriverCount,
}) {
  const [updating, setUpdating] = useState(false)

  async function updateAttendeeStatus(data) {
    toast.loading('Updating attendance...', { id: 'leave-event' })
    setUpdating(true)

    try {
      // Map {status, role} → named intent
      let intent = 'attending'
      if (data.status === 'withdrawn') {
        intent = 'withdrawn'
      } else if (data.role === 'reserve') {
        intent = 'reserve'
      }

      const result = await changeEventAttendanceAction(
        event.id,
        sessionUserId,
        intent,
      )

      if (!result.ok) throw new Error(result.message)
      // The protected joining-info control also appears on the race calendar,
      // where its event snapshot does not change with the attendance button.
      void globalMutate(
        (key) =>
          Array.isArray(key) &&
          key[0] === `/api/v4/events/${event.id}/server-info`,
      ).catch(() => undefined)

      // Handle registered
      if (
        increaseDriverCount &&
        data.status === 'registered' &&
        eventAttendee.status === 'withdrawn'
      ) {
        increaseDriverCount()
      }

      // Handle withdrawn
      if (
        decreaseDriverCount &&
        data.status === 'withdrawn' &&
        eventAttendee.status === 'registered'
      ) {
        decreaseDriverCount()
      }

      // Update local SWR cache
      await mutate()

      // Invalidate all related cache entries across the application
      const attendeeUrl = `community/${event.championship.communityId}/championship/${event.championshipId}/attendee`
      await globalMutate(
        (key) => typeof key === 'object' && key.url?.startsWith(attendeeUrl),
      )

      // Also invalidate event-specific data that other components might be using
      const eventUrl = `community/${event.championship.communityId}/championship/${event.championshipId}/event/${event.id}`
      await globalMutate(
        (key) => typeof key === 'object' && key.url?.startsWith(eventUrl),
      )

      if (mutateEventDrivers) {
        await mutateEventDrivers()
      }

      toast.success('Attendance updated!', { id: 'leave-event' })
    } catch (e) {
      toast.error(e.message, { id: 'leave-event' })
    } finally {
      setUpdating(false)
    }
  }

  const { status, role } = eventAttendee

  return (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger asChild>
        <Button
          type="button"
          className={cn('h-full min-h-[44px] w-full', {
            'border-success-500! bg-success-500! hover:border-success-600! hover:bg-success-600!':
              status === 'registered' && role !== 'reserve',
            'border-error-500! bg-error-500! hover:border-error-600! hover:bg-error-600!':
              status === 'withdrawn' && role !== 'reserve',
            'border-warning-500! bg-warning-500! hover:border-warning-600! hover:bg-warning-600!':
              role === 'reserve',
          })}
          disabled={updating}
        >
          {role === 'reserve' && status === 'registered'
            ? 'Reserve'
            : status === 'registered'
              ? "I'm Attending"
              : 'Not Attending'}

          <ChevronDown className="ml-1 h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="center">
        <DropdownMenuItem
          className="cursor-pointer"
          onClick={() =>
            updateAttendeeStatus({ status: 'registered', role: 'driver' })
          }
          disabled={status === 'registered' && role !== 'reserve'}
        >
          I'm Attending
        </DropdownMenuItem>
        <DropdownMenuItem
          className="cursor-pointer"
          onClick={() =>
            updateAttendeeStatus({ status: 'withdrawn', role: 'driver' })
          }
          disabled={status === 'withdrawn' && role !== 'reserve'}
        >
          Not Attending
        </DropdownMenuItem>
        <DropdownMenuItem
          className="cursor-pointer"
          onClick={() =>
            updateAttendeeStatus({ status: 'registered', role: 'reserve' })
          }
          disabled={role === 'reserve'}
        >
          Reserve
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
